import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Loader2, CheckCircle, Salad, User, Upload, FileText, Image, File } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssignMealPlan({ open, onClose, patient, onAssigned }) {
  const [step, setStep] = useState('select'); // 'select' | 'success'
  const [mode, setMode] = useState('template'); // 'template' | 'upload'
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [notes, setNotes] = useState('');
  const [assignment, setAssignment] = useState(null);
  
  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [planName, setPlanName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [extractedDays, setExtractedDays] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setStep('select');
      setMode('template');
      setSelectedTemplate('');
      setNotes('');
      setError('');
      setUploadedFile(null);
      setPlanName('');
      setExtractedDays(0);
    }
  }, [open]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/meal-plans/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      setError('Failed to load meal plan templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedTemplate) {
      setError('Please select a meal plan template');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      
      const response = await fetch(`${API_URL}/api/meal-plans/assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patient.patient_id,
          template_id: selectedTemplate,
          start_date: today,
          notes: notes || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setAssignment(data);
        setStep('success');
        if (onAssigned) onAssigned();
        toast.success('Meal plan assigned successfully!');
      } else {
        setError(data.detail || 'Failed to assign meal plan');
      }
    } catch (err) {
      setError('Failed to assign meal plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/webp',
        'image/heic'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|png|jpg|jpeg|webp|heic)$/i)) {
        setError('Please upload a PDF, Word document, or image file');
        return;
      }
      
      setUploadedFile(file);
      setError('');
      
      // Auto-generate plan name from file name
      if (!planName) {
        const baseName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
        setPlanName(baseName);
      }
    }
  };

  const handleUploadAndAssign = async () => {
    if (!uploadedFile) {
      setError('Please select a file to upload');
      return;
    }
    if (!planName.trim()) {
      setError('Please enter a name for the meal plan');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(uploadedFile);
      
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1]; // Remove data:...;base64, prefix
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/meal-plans/dietitian/upload-for-patient`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            patient_id: patient.patient_id,
            name: planName.trim(),
            file_base64: base64Data,
            file_type: uploadedFile.type || 'application/octet-stream',
            file_name: uploadedFile.name,
            notes: notes || null
          })
        });

        const data = await response.json();

        if (response.ok) {
          setExtractedDays(data.days_extracted || 0);
          setAssignment({
            template_name: data.plan_name,
            description: data.description,
            days_extracted: data.days_extracted
          });
          setStep('success');
          if (onAssigned) onAssigned();
          toast.success(`Meal plan uploaded! ${data.days_extracted} days extracted.`);
        } else {
          setError(data.detail || 'Failed to upload meal plan');
        }
        setUploading(false);
      };
      
      reader.onerror = () => {
        setError('Failed to read file');
        setUploading(false);
      };
    } catch (err) {
      setError('Failed to upload meal plan. Please try again.');
      setUploading(false);
    }
  };

  const handleClose = () => {
    setStep('select');
    setMode('template');
    setSelectedTemplate('');
    setNotes('');
    setError('');
    setAssignment(null);
    setUploadedFile(null);
    setPlanName('');
    setExtractedDays(0);
    onClose();
  };

  const getFileIcon = (file) => {
    if (!file) return <Upload className="w-8 h-8 text-gray-400" />;
    if (file.type.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (file.type.includes('image')) return <Image className="w-8 h-8 text-blue-500" />;
    return <File className="w-8 h-8 text-blue-500" />;
  };

  const selectedTemplateData = templates.find(t => t.template_id === selectedTemplate);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {step === 'select' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Salad className="w-5 h-5 text-green-500" />
                Assign Meal Plan
              </DialogTitle>
              <DialogDescription>
                Choose a template or upload a custom meal plan for {patient?.name || 'this patient'}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Patient Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{patient?.name}</p>
                  <p className="text-sm text-gray-500">{patient?.email}</p>
                </div>
              </div>

              {/* Tabs: Template vs Upload */}
              <Tabs value={mode} onValueChange={setMode} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="template" className="text-sm">
                    <Salad className="w-4 h-4 mr-2" />
                    From Template
                  </TabsTrigger>
                  <TabsTrigger value="upload" className="text-sm">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Custom
                  </TabsTrigger>
                </TabsList>

                {/* Template Selection Tab */}
                <TabsContent value="template" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Meal Plan Template</Label>
                    {loadingTemplates ? (
                      <div className="flex items-center gap-2 p-3 border rounded-lg">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm text-gray-500">Loading templates...</span>
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="p-3 border rounded-lg text-center text-gray-500 text-sm">
                        No templates available. Use the Upload option instead.
                      </div>
                    ) : (
                      <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                        <SelectTrigger data-testid="template-select">
                          <SelectValue placeholder="Select a meal plan..." />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(template => (
                            <SelectItem key={template.template_id} value={template.template_id}>
                              <div className="flex flex-col">
                                <span>{template.name}</span>
                                <span className="text-xs text-gray-500">
                                  {template.calorie_level} kcal • {template.cuisine_type}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Selected Template Preview */}
                  {selectedTemplateData && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                      <p className="font-medium text-green-800">{selectedTemplateData.name}</p>
                      <p className="text-sm text-green-600 mt-1">{selectedTemplateData.description}</p>
                      <div className="flex gap-2 mt-2">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          {selectedTemplateData.calorie_level} kcal
                        </span>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded capitalize">
                          {selectedTemplateData.cuisine_type}
                        </span>
                        {selectedTemplateData.diabetes_type && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded capitalize">
                            {selectedTemplateData.diabetes_type}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Upload Tab */}
                <TabsContent value="upload" className="space-y-4 mt-4">
                  {/* Plan Name */}
                  <div className="space-y-2">
                    <Label htmlFor="planName">Meal Plan Name *</Label>
                    <Input
                      id="planName"
                      value={planName}
                      onChange={(e) => setPlanName(e.target.value)}
                      placeholder="e.g., 2-Week Diabetic Meal Plan"
                      data-testid="plan-name-input"
                    />
                  </div>

                  {/* File Upload Area */}
                  <div className="space-y-2">
                    <Label>Upload Meal Plan File</Label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                        uploadedFile 
                          ? 'border-green-300 bg-green-50' 
                          : 'border-gray-300 hover:border-green-400 hover:bg-green-50/50'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.heic,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="file-upload-input"
                      />
                      
                      <div className="flex flex-col items-center gap-2">
                        {getFileIcon(uploadedFile)}
                        {uploadedFile ? (
                          <>
                            <p className="font-medium text-green-700">{uploadedFile.name}</p>
                            <p className="text-xs text-green-600">
                              {(uploadedFile.size / 1024).toFixed(1)} KB • Click to change
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-gray-600">Click to upload</p>
                            <p className="text-xs text-gray-500">
                              PDF, Word doc, or Image (max 10MB)
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Notes (shown for both modes) */}
              <div className="space-y-2">
                <Label htmlFor="notes">Personal Notes for Patient (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="assignment-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any special instructions or encouragement..."
                  rows={2}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0 mt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              {mode === 'template' ? (
                <Button 
                  onClick={handleAssign}
                  data-testid="assign-plan-btn"
                  className="bg-green-500 hover:bg-green-600"
                  disabled={loading || !selectedTemplate}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    'Assign Plan'
                  )}
                </Button>
              ) : (
                <Button 
                  onClick={handleUploadAndAssign}
                  data-testid="upload-plan-btn"
                  className="bg-green-500 hover:bg-green-600"
                  disabled={uploading || !uploadedFile || !planName.trim()}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading & Extracting...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload & Assign
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                Meal Plan Assigned!
              </DialogTitle>
              <DialogDescription>
                {patient?.name} now has access to their personalized meal plan.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="font-medium text-green-800">{assignment?.template_name}</p>
                <p className="text-sm text-green-600 mt-1">
                  {assignment?.days_extracted 
                    ? `${assignment.days_extracted} days extracted • Starting today`
                    : `${assignment?.calorie_level || ''} kcal • Starting today`
                  }
                </p>
                {assignment?.description && (
                  <p className="text-xs text-green-500 mt-2">{assignment.description}</p>
                )}
              </div>

              <p className="text-sm text-gray-500 text-center">
                The patient will see their meal plan on their dashboard with daily recommendations.
              </p>
            </div>

            <DialogFooter className="mt-4">
              <Button 
                onClick={handleClose}
                className="w-full bg-green-500 hover:bg-green-600"
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
